import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { contractorAuth, portalContractor } from '../lib/contractorAuth';
import { requestWord } from '../lib/requestState';
import { fmtDate } from '../lib/when';
// The card is the portal's own (.ac-card / .cd-request), so a request looks
// the same here as it does under "Raised by me" on the My Tickets tab.
import './ContractorDashboard.css';
import './ContractorRequests.css';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * My Requests: only the tickets this contractor RAISED.
 *
 * Gordon, 22 October: "when i click on tickets on the top bar and then on my
 * tickets - it should show the tickets that the contractor opened and not all
 * the assigned tickets." The bar's Tickets ▾ used to open the portal on its
 * My Tickets tab, which is every job assigned to them with the requests
 * underneath - the whole picture, and the right one for that tab, which keeps
 * it. This page is the other answer: the requests to the firm they made
 * themselves (migration 064), newest first, and nothing else.
 *
 * Same list the portal reads - GET /contractors/:id/assignments carries both
 * kinds of row - filtered to raised_by_me. A request the firm accepted and
 * assigned back to them is still something they asked for, so it stays here
 * (the portal, which is about work to do, moves it to the jobs). The word on
 * each card is lib/requestState's, shared with the portal and the ticket page,
 * so the three cannot disagree about where a request stands.
 *
 * An administrator looking through View as sees the contractor's, and opens
 * one on the contractor's own ticket page, as the portal does: the server
 * admits an administrator naming the contractor.
 */
export default function ContractorRequests() {
  const navigate = useNavigate();
  const who = useMemo(() => portalContractor(), []);
  const auth = useMemo(() => contractorAuth(), []);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!who?.id) { setLoading(false); return undefined; }
    let live = true;
    axios.get(`${API_URL}/contractors/${who.id}/assignments`, auth)
      .then((r) => {
        if (!live) return;
        const rows = Array.isArray(r.data) ? r.data : (r.data?.assignments ?? []);
        setRequests(rows
          .filter((t) => t.raised_by_me === true)
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
        setLoading(false);
      })
      .catch((err) => {
        if (!live) return;
        const status = err.response?.status;
        if (status === 401 && !who.viewing) { navigate('/contractor-login'); return; }
        setProblem(status === 401 || status === 403
          ? 'Not permitted to read this contractor’s requests.'
          : status
            ? `Could not load your requests (server returned ${status}).`
            : 'Could not reach the server to load your requests.');
        setLoading(false);
      });
    return () => { live = false; };
  }, [auth, who, navigate]);

  // The contractor's own page for it, for an administrator looking through
  // View as too: /my-tickets/:id draws it for both (App.js), so "see what
  // they see" is what they see rather than the staff page with its controls.
  const open = (id) => navigate(`/my-tickets/${id}`);

  // How many are still waiting on somebody's yes: the one number worth
  // putting at the top, because it is the thing a contractor comes here to
  // find out.
  const pending = requests.filter((t) => requestWord(t).key === 'pending').length;

  const day = (d) => fmtDate(d) || '—';

  if (!who?.id) {
    return (
      <div className="crq-page">
        <div className="crq-wrap">
          <p className="crq-quiet">Sign in to the contractor portal to see your requests.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="crq-page">
      <div className="crq-wrap">
        <header className="crq-head">
          <div className="crq-head-text">
            <h1 className="crq-title">
              My requests
              {!loading && !problem && requests.length > 0 && (
                <span className="crq-count" aria-label={`${requests.length} requests`}>{requests.length}</span>
              )}
            </h1>
            <p className="crq-lead">
              {who.viewing
                ? <>What <strong>{who.name}</strong> has asked the firm for. Their assigned jobs are on the portal.</>
                : 'What you have asked the firm for — a subscription, a part, access to a system. Your assigned jobs are on the portal.'}
            </p>
          </div>
          {/* Not while the list is empty: the empty state below carries the
              one button, and two of the same on one screen is clutter. */}
          {(loading || problem || requests.length > 0) && (
            <Link className="btn btn-primary crq-new" to="/create">+ New request</Link>
          )}
        </header>

        {loading && <p className="crq-quiet" role="status">Loading your requests&hellip;</p>}

        {!loading && problem && <p className="crq-problem" role="alert">{problem}</p>}

        {!loading && !problem && requests.length === 0 && (
          <div className="crq-empty">
            <p className="crq-empty-title">You haven&rsquo;t raised a request yet</p>
            <p className="crq-empty-text">
              Need something for the work &mdash; a licence, a replacement part, an account?
              Raise a request and it goes to an approver.
            </p>
            <Link className="btn btn-primary crq-empty-new" to="/create">New request</Link>
          </div>
        )}

        {!loading && !problem && requests.length > 0 && (
          <>
            <p className="crq-summary">
              {pending === 0
                ? 'Nothing is waiting on an approver.'
                : `${pending} awaiting approval · newest first`}
            </p>
            <ul className="crq-list" aria-label="My requests">
              {requests.map((t) => {
                const word = requestWord(t);
                return (
                  <li key={t.id}>
                    <button type="button" className="ac-card cd-request crq-card"
                            data-decision={word.key}
                            onClick={() => open(t.id)}
                            aria-label={`Open ${t.ticket_number}: ${t.title || ''}`}>
                      <span className="ac-top">
                        <span className="ac-number">{t.ticket_number}</span>
                        <span className="cd-request-state">{word.label}</span>
                      </span>
                      <span className="ac-title">{t.title}</span>
                      <span className="ac-meta">
                        <span className="ac-field">
                          <span className="ac-field-label">Raised</span>
                          <span className="ac-field-value">{day(t.created_at)}</span>
                        </span>
                        {t.category && (
                          <span className="ac-field">
                            <span className="ac-field-label">Category</span>
                            <span className="ac-field-value">{t.category}</span>
                          </span>
                        )}
                        {t.priority && (
                          <span className="ac-field">
                            <span className="ac-field-label">Priority</span>
                            <span className="ac-field-value">{t.priority}</span>
                          </span>
                        )}
                        {/* Who it is with, when the row says. The list does
                            not always carry the approver; the ticket page
                            behind the card always does. */}
                        {t.approver_name && (
                          <span className="ac-field">
                            <span className="ac-field-label">Approver</span>
                            <span className="ac-field-value">{t.approver_name}</span>
                          </span>
                        )}
                      </span>
                      <span className="ac-open">Open &rsaquo;</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
